import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type {
    SelectedVesselCrewMember,
    VesselCrewMemberRequest,
} from '@routes/sidebar/admin-center/___shared___/dashboards/api/vesselCrewMember.api.ts';
import { postApiFetchVesselCrewMember } from '@routes/sidebar/admin-center/___shared___/dashboards/api/vesselCrewMember.api.ts';
import { VESSEL_CREW_MEMBER_INITIAL_STATE } from '@routes/sidebar/admin-center/___shared___/dashboards/model/constants.ts';
import type { VesselCrewMemberResponse } from '@routes/sidebar/admin-center/___shared___/dashboards/types.ts';
import { extractError } from '@utils/slice.ts';

interface VesselCrewMemberError {
    message?: string;
    [k: string]: any;
}

interface VesselCrewMemberState {
    error: VesselCrewMemberError | null;
    vesselCrewMember: VesselCrewMemberResponse;
    currentGetRequestId: string | null;
    selectedVesselCrewMember: SelectedVesselCrewMember | null;

    isGetVesselCrewMemberPending: boolean;
}

const initialState: VesselCrewMemberState = {
    error: null,
    vesselCrewMember: VESSEL_CREW_MEMBER_INITIAL_STATE,
    currentGetRequestId: null,
    selectedVesselCrewMember: null,

    isGetVesselCrewMemberPending: false,
};

export const getVesselCrewMember = createAsyncThunk<
    VesselCrewMemberResponse,
    { tenantAlias: string; crewUserId: number; payload: VesselCrewMemberRequest },
    { rejectValue: VesselCrewMemberError }
>('adminCenterVesselCrewMember/getVesselCrewMember', ({ tenantAlias, crewUserId, payload }, { rejectWithValue }) =>
    postApiFetchVesselCrewMember({ tenantAlias, crewUserId, payload })
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

const adminCenterVesselCrewMemberSlice = createSlice({
    name: 'adminCenterVesselCrewMember',
    initialState,
    reducers: {
        setSelectedVesselCrewMember(state, action: PayloadAction<SelectedVesselCrewMember | null>) {
            state.selectedVesselCrewMember = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getVesselCrewMember.pending, (state, action) => {
            state.isGetVesselCrewMemberPending = true;
            state.error = null;
            state.currentGetRequestId = action.meta.requestId;
        });
        builder.addCase(getVesselCrewMember.fulfilled, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetVesselCrewMemberPending = false;
            state.currentGetRequestId = null;
            state.vesselCrewMember = action.payload;
        });
        builder.addCase(getVesselCrewMember.rejected, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetVesselCrewMemberPending = false;
            state.currentGetRequestId = null;
            state.error = extractError(action);
        });
    },
});

export const { setSelectedVesselCrewMember } = adminCenterVesselCrewMemberSlice.actions;

export default adminCenterVesselCrewMemberSlice.reducer;
